// Browser entry of the YouTube transcript layer ("client mode": requests come from the user's IP).
//
// Why a special fetch: YouTube's /youtubei/v1/* endpoints answer cross-origin requests only when
// the page's Origin contains "youtube" or "google" (or is the opaque origin "null") - verified Oct 2026:
//   Origin https://youtube-summary.apps.twoc.top -> 200 + Access-Control-Allow-Origin
//   Origin http://localhost:5173 / https://example.com -> 403, no CORS headers -> "Failed to fetch"
// Our production origin qualifies (luckily, by its name). Everything else (localhost dev, other
// domains, a future rule change) falls back to a hidden sandboxed iframe: its opaque origin sends
// "Origin: null", which YouTube accepts. Dev tip: http://youtube-summary.localhost:5173 works directly.
// If the app sets a CSP: the srcdoc iframe inherits it, so it needs script-src 'unsafe-inline' (or a
// hash of the inline script) and connect-src https://www.youtube.com.

import type { VideoTranscript } from '../../shared/api';
import { fetchVideoTranscript, type FetchLike, type FetchTranscriptOptions } from '../../shared/youtube';

export type YouTubeFetchMode = 'auto' | 'direct' | 'sandbox';

/** fetchVideoTranscript() with the browser fetch strategy below. */
export function fetchVideoTranscriptInBrowser(
  videoId: string,
  options: Omit<FetchTranscriptOptions, 'clientHeaders' | 'headers' | 'transport'> = {},
): Promise<VideoTranscript> {
  return fetchVideoTranscript(videoId, { ...options, fetch: options.fetch ?? getYouTubeBrowserFetch() });
}

let shared: FetchLike | undefined;
/** Process-wide instance, so the direct/sandbox decision is made once per page. */
export function getYouTubeBrowserFetch(): FetchLike {
  return (shared ??= createYouTubeBrowserFetch());
}

export function createYouTubeBrowserFetch(mode: YouTubeFetchMode = 'auto'): FetchLike {
  let useSandbox = mode === 'sandbox' || (mode === 'auto' && !/youtube|google/i.test(location.origin));
  return async (input, init) => {
    if (!useSandbox) {
      try {
        return await fetch(input, init);
      } catch (error) {
        // A CORS rejection (403 without CORS headers) is indistinguishable from a network error.
        if (mode === 'direct' || isAbort(error)) throw error;
        useSandbox = true;
      }
    }
    return sandboxFetch(input, init);
  };
}

// ---- sandboxed iframe relay -------------------------------------------------------------------

const SANDBOX_HTML = `<!doctype html><meta charset="utf-8"><script>
const allowed = /^https:\\/\\/(?:[a-z0-9-]+\\.)*(?:youtube\\.com|googleapis\\.com)\\//;
const controllers = new Map();
addEventListener('message', async (event) => {
  if (event.source !== parent) return;
  const { id, type, url, method, headers, body } = event.data || {};
  if (type === 'abort') { controllers.get(id)?.abort(); return; }
  if (type !== 'fetch') return;
  const reply = (data) => parent.postMessage({ id, ...data }, '*');
  if (!allowed.test(url)) return reply({ error: 'URL not allowed', name: 'TypeError' });
  const controller = new AbortController();
  controllers.set(id, controller);
  try {
    const r = await fetch(url, { method, headers, body, credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal });
    reply({ status: r.status, statusText: r.statusText, contentType: r.headers.get('content-type'), body: await r.text() });
  } catch (e) {
    reply({ error: String((e && e.message) || e), name: (e && e.name) || 'TypeError' });
  } finally {
    controllers.delete(id);
  }
});
parent.postMessage({ type: 'ready' }, '*');
<\/script>`;

interface Pending {
  resolve: (response: Response) => void;
  reject: (error: unknown) => void;
}

let sandboxWindow: Promise<Window> | undefined;
const pending = new Map<number, Pending>();
let nextId = 1;

function getSandbox(): Promise<Window> {
  sandboxWindow ??= new Promise<Window>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('sandbox', 'allow-scripts'); // no allow-same-origin -> opaque origin
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.style.display = 'none';
    iframe.srcdoc = SANDBOX_HTML;
    const timer = setTimeout(() => {
      sandboxWindow = undefined;
      iframe.remove();
      reject(new TypeError('YouTube sandbox iframe did not start (blocked by CSP?)'));
    }, 5000);
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow || typeof event.data !== 'object' || !event.data) return;
      const data = event.data as {
        type?: string;
        id?: number;
        error?: string;
        name?: string;
        status?: number;
        statusText?: string;
        contentType?: string | null;
        body?: string;
      };
      if (data.type === 'ready') {
        clearTimeout(timer);
        resolve(iframe.contentWindow!);
        return;
      }
      const entry = data.id !== undefined ? pending.get(data.id) : undefined;
      if (!entry || data.id === undefined) return;
      pending.delete(data.id);
      if (data.error !== undefined) {
        entry.reject(data.name === 'AbortError' ? new DOMException(data.error, 'AbortError') : new TypeError(data.error));
        return;
      }
      const status = data.status ?? 0;
      const nullBody = [101, 103, 204, 205, 304].includes(status);
      entry.resolve(
        new Response(nullBody ? null : (data.body ?? ''), {
          status,
          statusText: data.statusText,
          headers: data.contentType ? { 'content-type': data.contentType } : {},
        }),
      );
    });
    (document.body ?? document.documentElement).appendChild(iframe);
  });
  return sandboxWindow;
}

async function sandboxFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const signal = init.signal ?? undefined;
  signal?.throwIfAborted();
  if (init.body != null && typeof init.body !== 'string') throw new TypeError('sandboxFetch only supports string bodies');
  const target = await getSandbox();
  const id = nextId++;
  return new Promise<Response>((resolve, reject) => {
    const onAbort = () => {
      pending.delete(id);
      target.postMessage({ type: 'abort', id }, '*');
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    pending.set(id, {
      resolve: (r) => {
        signal?.removeEventListener('abort', onAbort);
        resolve(r);
      },
      reject: (e) => {
        signal?.removeEventListener('abort', onAbort);
        reject(e);
      },
    });
    signal?.addEventListener('abort', onAbort, { once: true });
    target.postMessage(
      {
        type: 'fetch',
        id,
        url: input,
        method: init.method ?? 'GET',
        headers: Object.fromEntries(new Headers(init.headers).entries()),
        body: init.body ?? undefined,
      },
      '*',
    );
  });
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'AbortError' || error.name === 'TimeoutError');
}
